import React, { useContext, useEffect } from 'react';

import { Box } from '@uhg-abyss/web/ui/Box';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Table } from '@src/common/Table';
import { DEFAULT_REPORT_TYPE } from '@src/components/Reports/Reports';
import { useGetChildFilesQuery } from '@src/store/services/cdb/onlineServices';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { useAuth } from '@src/Providers/AuthProvider';
import { FileContext } from '@src/routes/Routes';
import { statusListArr } from './data';
import { LoadingScreen } from '@src/common/LoadingScreen';
import { mapV1ColumnsToV2, withRowIds } from '@src/utils/tableUtils';
import { encryptProfileParams } from '@src/utils';

const ChildTable = ({ row }) => {
  const { user } = useAuth();

  const { data, isFetching, isLoading } = useGetChildFilesQuery({
    fileId: row?.original?.fileID,
    searchText: row?.original?.searchText ?? '',
    userType: user?.userType,
    userId: user?.userId,
  });
  const { setFileData } = useContext(FileContext);
  const columns = React.useMemo(() => {
    return [
      {
        Header: 'File ID',
        accessor: 'transSetId',
        minWidth: 60,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          return <strong>{value}</strong>;
        },
      },
      {
        Header: 'Customer Name',
        accessor: 'customerName',
        minWidth: 250,
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        Cell: ({ value, row }) => {
          if (!user?.viewProfile) return <strong>{value}</strong>;
          return (
            <Link
              href={`/profile/${encryptProfileParams(
                row?.original?.prflverNbr?.trim(),
                row?.original?.asgnSbmtId?.trim(),
                row?.original?.eligSysCode?.trim()
              )}`}
            >
              {value}
            </Link>
          );
        },
      },
      {
        Header: 'Received',
        minWidth: 220,
        accessor: 'fileRecvDate',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          return <strong>{value}</strong>;
        },
      },
      {
        Header: 'Status',
        accessor: 'fileStatus',
        minWidth: 250,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          return <strong>{value}</strong>;
        },
      },
      {
        Header: 'File Name',
        accessor: 'fileName',
        minWidth: 250,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ value }) => {
          return <strong>{value}</strong>;
        },
      },
      {
        Header: 'Options',
        accessor: '',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        label: 'Reports',
        Cell: ({ row }) => {
          const isStatus = statusListArr.includes(
            row?.values?.fileStatus?.trim()
          );

          const handleClick = () => {
            return setFileData((prevData) => {
              return { ...prevData, ...row.original };
            });
          };

          return (
            <Flex
              css={{
                gap: '10px',
              }}
            >
              {isStatus ? (
                <strong>RPT</strong>
              ) : (
                <React.Fragment>
                  <Link
                    onClick={handleClick}
                    href={`/home/reports/${row.original.transSetId}/${DEFAULT_REPORT_TYPE}`}
                  >
                    RPT
                  </Link>
                  |
                </React.Fragment>
              )}
              {user?.viewMntr ? (
                <Link
                  href={`/home/monitor/${row.original.transSetId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mntr
                </Link>
              ) : (
                <strong>Mntr</strong>
              )}
            </Flex>
          );
        },
      },
    ];
  }, [setFileData, user]);

  const v2Columns = React.useMemo(() => mapV1ColumnsToV2(columns), [columns]);

  const dataTableProps = useDataTable<any>({
    initialData: [] as any[],
    initialColumns: v2Columns,
    rowIdKey: 'uniqueId',
    paginationConfig: { enablePagination: false },
  });

  useEffect(() => {
    if (v2Columns?.length) {
      dataTableProps.setColumns(v2Columns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2Columns]);

  useEffect(() => {
    if (data) {
      dataTableProps.setData(withRowIds(data?.dashboardFileInfoList));
    }
  }, [data]);

  if (isFetching || isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Box
      css={{
        padding: 0,
        '.abyss-data-table-head': {
          display: 'none',
        },
      }}
    >
      <DataTable
        title=""
        hideHeader
        tableState={dataTableProps}
        css={{ width: '100%' }}
      >
        <DataTable.Table />
      </DataTable>
    </Box>
  );
};

export default ChildTable;
